import { stringify } from "qs";
import { useCallback, useEffect, useRef, useState } from "react";
import useSWR, { mutate, SWRConfig } from "swr";
import { timeStrToMinutes } from "./lib/date-utils";
import { errorToString } from "./lib/error-utils";

export const MODELS = {
  ME: `/auth/me`,
  INVITE: (secret) => `/auth/invites/${secret}`,
  RESET_PW_TOKEN_INFO: (token) => `/auth/reset-token/${token}`,

  // Admin Area
  ADMIN_USERS: `/auth/admin`,

  WORKSHOPS: (archived) =>
    `/workshops${stringify({ archived }, { addQueryPrefix: true })}`,
  WORKSHOP: ({ id, slug }) =>
    id ? `/workshop/${id}` : `/workshop?slug=${slug}`,
  WORKSHOP_PENDING_INVITES: (workshopId) =>
    `/workshops/${workshopId}/invites/pending`, // teacher invites
  WORKSHOP_SETTINGS: (workshopId) => `/workshops/${workshopId}/settings`,
  WORKSHOP_STUDENT_INVITE: (workshopId) => `/workshops/${workshopId}/invites/student`,

  SPARKS: `/sparks`,
  SPARK_MODULES: (sparkId) => `/sparks/${sparkId}/modules`,
  SPARK_SCHEDULE: (sparkSlug) => `/spark/${sparkSlug}/schedule`,

  WORKSHOP_SPARK_TEMPLATES: `/templates`,
  WORKSHOP_SPARK_TEMPLATE: ({ id, sparkSlug }) => {
    if (id) return `/template/${id}`;
    if (sparkSlug) return `/template?sparkSlug=${sparkSlug}`;
    return null;
  },
  TEMPLATE_SETTINGS: (templateId) => `/template/${templateId}/settings`,

  PUBLIC_SHOWOROOMS: `/public-showrooms`,
  PEERTUBE_VIDEO_FILE_URL: (videoId) => `/peertube/video/${videoId}/hls`,

  S3_FILE_URL: (filepath) => `/upload?filepath=${encodeURIComponent(filepath)}`,

  WORKSHOP_USER_INFO: (workshopId) => `/workshops/${workshopId}/user-info`,
  WORKSHOP_SMALL_TEAMS: (workshopId) => `/workshops/${workshopId}/small-teams`,

  // SPARKS

  SPARKS_INFO: `/sparks/info`,
  SPARK_MODULE_CONTENT: (key) => `/spark-module-content/${key}`,

  SMALL_TEAM: ({ workshopId, smallTeamId }) =>
    `/small-team${smallTeamId ? "/" + smallTeamId : "?workshopId=" + workshopId}`,
  SMALL_TEAM_MEMBERS: (smallTeamId) => `/small-team/${smallTeamId}/members`,

  WORKSHOP_BY_INVITE_SECRET: (secret) => `/auth/invites/${secret}/workshop`,

  WORKSHOP_SPARK_SHOWROOM_ENTRIES: (workshopSparkId) =>
    `/showroom-entries?workshopSpark=${workshopSparkId}`,
  WORKSHOP_SPARK_UNSEEN_COMMENTS: (workshopSparkId) =>
    `/workshop-spark/${workshopSparkId}/unseen-comments`,
  SPARK_SHARED_SHOWROOM_ENTRIES: (sparkId) =>
    `/showroom-entries?spark=${sparkId}`,

  SMALL_TEAM_SHOWROOM_ENTRY: (smallTeamId) =>
    `/teams/${smallTeamId}/showroom-entry`,

  WORKSHOPS_MINE: `/me/workshops`,

  // LIBRARY

  LIBRARY_ITEMS: `/library`,

  // PUBLIC SHOWROOM

  PUBLIC_SHOWROOM_INFO: (slug) => `/public-showroom/${slug}`,

  // One Off

  _KUJG_CREATE_SOLUTION_TEAM_INFO: (showroomEntryId) =>
    `/_kujg/create-solution-team/${showroomEntryId}/info`,
};

export const MUTATIONS = {
  login: ({ username, tag, password, email }) =>
    post("/auth/login", { username, tag, password, email }, [MODELS.ME]),
  logout: () => post("/auth/logout", {}, [MODELS.ME]),
  register: ({ username, email, password }) =>
    post(`/auth/register`, { username, email, password }, [MODELS.ME]),

  resetPasswordRequest: ({ email }) =>
    post(`/auth/reset-password-request`, { email }, [MODELS.ME]),
  createResetPasswordLink: ({ userId }) =>
    post(`/users/${userId}/create-password-reset-link`, {}, []),
  resetPassword: ({ token, password }) =>
    post(`/auth/reset-password/${token}`, { password }, [MODELS.ME]),

  applyInvite: ({ secret, password, username, email }) =>
    post(`/auth/invites/${secret}/apply`, { password, username, email }, [
      MODELS.ME,
    ]),
  applyInviteAndAddUserToNewSmallTeam: ({ secret, password, name, email }) =>
    post(
      `/auth/invites/${secret}/apply?addToSmallTeam=true`,
      { password, username: name, email },
      [MODELS.ME, MODELS.INVITE(secret)], // This would also update the MODELS.WORKSHOP_USER_INFO(workshopId), MODELS.WORKSHOP_SMALL_TEAMS(workshopId) but we don't have workshopId.
    ),
  applyInviteToAuthUser: ({ secret }) =>
    post(`/auth/invites/${secret}/apply-existing-user`, {}, [MODELS.ME]),
  makeAdmin: ({ email }) =>
    post(`/auth/admin`, { email }, [MODELS.ADMIN_USERS]),
  unmakeAdmin: ({ id }) => post(`/auth/admin/${id}/delete`, {}, [MODELS.ADMIN_USERS]),
  updateProfileImage: ({ profileImageData }) =>
    post("/auth/update-profile-image", { profileImageData }, [MODELS.ME]),
  updatePassword: ({ oldPassword, newPassword }) =>
    post("/auth/update-password", { oldPassword, newPassword }, []),
  updateUserDetails: ({ name, email }) =>
    post("/auth/update-user-details", { name, email }, [MODELS.ME]),
  deleteMe: () => post("/user/delete", {}, [MODELS.ME]),

  removeUserFromTeam: ({ teamId, userId, workshopId }) =>
    post(`/teams/${teamId}/remove-user`, { userId, workshopId }, [
      MODELS.WORKSHOP_USER_INFO(workshopId),
    ]),

  createTemplate: ({ name, slug, sparkId }) =>
    post("/templates/create", { name, slug, sparkId }, [
      MODELS.WORKSHOPS(false),
      MODELS.WORKSHOPS(true),
    ]),
  createWorkshopForTSS: ({
    name,
    workshopSparkTemplateId,
    schoolType,
    schoolLocation,
    schoolName,
    classSize,
  }) =>
    post(
      "/workshop",
      {
        name,
        workshopSparkTemplateId,
        schoolType,
        schoolLocation,
        schoolName,
        classSize,
      },
      [MODELS.WORKSHOPS(false)],
    ),
  inviteTeacher: ({ id, email }) =>
    post(`/workshops/${id}/inviteTeacher`, { email }, [
      MODELS.WORKSHOP_USER_INFO(id),
      MODELS.WORKSHOP_PENDING_INVITES(id),
    ]),

  updateWorkshop: ({
    id,
    name,
    slug,
    isArchived,
    publicShowroomId,
  }) =>
    post(
      `/workshops/${id}/update`,
      {
        name,
        slug,
        isArchived,
        publicShowroomId,
      },
      [
        MODELS.WORKSHOPS(false),
        MODELS.WORKSHOPS(true),
        MODELS.WORKSHOP({ id }),
      ],
    ),
  transferWorkshopOwnership: ({ id, userId }) =>
    post(`/workshops/${id}/transfer`, { userId }, [MODELS.WORKSHOP({ id })]),
  deleteWorkshop: ({ id }) =>
    post(`/workshops/${id}/delete`, {}, [MODELS.WORKSHOPS(false)]),
  updateTemplateSchedule: ({ sparkSlug, schedule, contentGroups }) =>
    post(
      `/spark/${sparkSlug}/schedule`,
      { schedule, contentGroups },
      [
        MODELS.SPARK_SCHEDULE(sparkSlug),
        MODELS.WORKSHOPS(false),
        MODELS.WORKSHOPS(true),
      ],
    ),
  updateWorkshopSettings: ({
    workshopId,
    libraryEnabled,
    commentsEnabled,
    projectSortMode,
  }) =>
    post(
      `/workshops/${workshopId}/settings`,
      { libraryEnabled, commentsEnabled, projectSortMode },
      [MODELS.WORKSHOP_SETTINGS(workshopId)],
    ),
  createSmallTeam: ({ workshopId, extToolUrl, name, workshopSparkId }) =>
    post(
      `/workshop-spark/${workshopSparkId}/small-team`,
      { extToolUrl, name },
      [MODELS.WORKSHOP_SMALL_TEAMS(workshopId)],
    ),
  addUserToSmallTeam: ({ workshopId, userId, smallTeamId }) =>
    post(`/workshops/add-to-small-team`, { userId, smallTeamId }, [
      MODELS.WORKSHOP_USER_INFO(workshopId),
    ]),
  deleteUserFromSmallTeam: ({ workshopId, userId, smallTeamId }) =>
    post(`/workshops/delete-from-small-team`, { userId, smallTeamId }, [
      MODELS.WORKSHOP_USER_INFO(workshopId),
    ]),
  updateSmallTeamInfo: ({ workshopId, smallTeamId, name }) =>
    post(`/small-team/${smallTeamId}`, { name }, [
      MODELS.WORKSHOP_SMALL_TEAMS(workshopId),
    ]),
  deleteSmallTeam: ({ workshopId, smallTeamId }) =>
    post(`/team/${smallTeamId}/delete`, {}, [
      MODELS.WORKSHOP_USER_INFO(workshopId),
      MODELS.WORKSHOP_SMALL_TEAMS(workshopId),
    ]),

  createPublicShowroom: ({ title, slug, description, flags }) =>
    post("/public-showrooms/create", { title, slug, description, flags }, [
      MODELS.PUBLIC_SHOWOROOMS,
    ]),
  updatePublicShowroom: ({ id, title, slug, description, flags }) =>
    post(
      `/public-showrooms/${id}/update`,
      { title, slug, description, flags },
      [MODELS.PUBLIC_SHOWOROOMS],
    ),
  deletePublicShowroom: ({ id }) =>
    post(`/public-showrooms/${id}/delete`, {}, [MODELS.PUBLIC_SHOWOROOMS]),
  setShowroomEntry: ({
    smallTeamId,
    workshopSparkId,
    formData,
    isPublished,
    agreedOnFutureUse,
  }) =>
    post(
      `/teams/${smallTeamId}/showroom-entry`,
      {
        smallTeamId,
        workshopSparkId,
        isPublished,
        agreedOnFutureUse,
        formData,
      },
      [
        MODELS.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId),
        MODELS.WORKSHOP_SPARK_SHOWROOM_ENTRIES(workshopSparkId),
      ],
    ),

  addComment: ({
    entryId,
    content,
    parentCommentId,
    smallTeamId,
    workshopSparkId,
    sparkId,
    token,
    name,
  }) =>
    post(
      `/showroom-entry/${entryId}/add-comment`,
      { content, parentCommentId, token, name },
      [
        MODELS.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId),
        MODELS.WORKSHOP_SPARK_SHOWROOM_ENTRIES(workshopSparkId),
        ...(sparkId ? [MODELS.SPARK_SHARED_SHOWROOM_ENTRIES(sparkId)] : []),
        ...(token ? [MODELS.ME] : []),
      ],
    ),
  updateComment: ({ commentId, content, smallTeamId }) =>
    post(`/comments/${commentId}/update`, { content }, [
      MODELS.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId),
    ]),
  // only for admins
  deleteComment: ({ commentId, smallTeamId }) =>
    post(`/comments/${commentId}/delete`, {}, [
      MODELS.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId),
    ]),

  addReaction: ({ entryId, reaction, smallTeamId, workshopSparkId, sparkId }) =>
    post(`/showroom-entry/${entryId}/add-reaction`, { reaction }, [
      MODELS.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId),
      MODELS.WORKSHOP_SPARK_SHOWROOM_ENTRIES(workshopSparkId),
      ...(sparkId ? [MODELS.SPARK_SHARED_SHOWROOM_ENTRIES(sparkId)] : []),
    ]),
  removeReaction: ({
    entryId,
    reaction,
    smallTeamId,
    workshopSparkId,
    sparkId,
  }) =>
    post(`/showroom-entry/${entryId}/remove-reaction`, { reaction }, [
      MODELS.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId),
      MODELS.WORKSHOP_SPARK_SHOWROOM_ENTRIES(workshopSparkId),
      ...(sparkId ? [MODELS.SPARK_SHARED_SHOWROOM_ENTRIES(sparkId)] : []),
    ]),
  addCommentReaction: ({ commentId, reaction, smallTeamId }) =>
    post(`/comments/${commentId}/add-reaction`, { reaction }, [
      MODELS.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId),
    ]),
  removeCommentReaction: ({ commentId, reaction, smallTeamId }) =>
    post(`/comments/${commentId}/remove-reaction`, { reaction }, [
      MODELS.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId),
    ]),
  addVisit: ({ entryId, smallTeamId, workshopSparkId, sparkId }) =>
    post(`/showroom-entry/${entryId}/visit`, {}, [
      MODELS.SMALL_TEAM_SHOWROOM_ENTRY(smallTeamId),
      MODELS.WORKSHOP_SPARK_SHOWROOM_ENTRIES(workshopSparkId),
      ...(sparkId ? [MODELS.SPARK_SHARED_SHOWROOM_ENTRIES(sparkId)] : []),
    ]),
  seenComment: ({ commentId, workshopSparkId }) =>
    post(`/comments/${commentId}/seen`, {}, [
      MODELS.WORKSHOP_SPARK_UNSEEN_COMMENTS(workshopSparkId),
    ]),

  createPeertubeToken: () => post("/peertube/create-upload-token", {}, []),

  _kujgCreateSolutionTeam: ({ showroomEntryId, members }) =>
    post(
      `/_kujg/create-solution-team/${showroomEntryId}/create`,
      { members },
      [],
    ),
};

export const useModel = (model) => {
  const { url, options } =
    !model || typeof model === "string" ? { url: model, options: {} } : model;
  const { data, error } = useSWR(url, undefined, options);
  if (error) {
    console.error("useModel error:", error);
  }
  return data;
};

export const useMutation = (handler) => {
  if (!handler) throw new Error("handler can't be empty");
  const [state, setState] = useState("initial");
  const lastHandlerRef = useRef(handler);
  useEffect(() => {
    lastHandlerRef.current = handler;
  }, [handler]);
  const lastStateRef = useRef(state);
  useEffect(() => {
    lastStateRef.current = state;
  }, [state]);
  const mutationHandler = useCallback((data) => {
    if (lastStateRef.current !== "initial") return;
    setState("loading");
    return lastHandlerRef.current(data).then(
      (result) => {
        setState("initial");
        return result;
      },
      (e) => {
        setState("initial");
        return Promise.reject(e);
      },
    );
  }, []);
  return [mutationHandler, { isLoading: state === "loading" }];
};

const processBody = (data) => {
  if (data) {
    if (Array.isArray(data)) data.forEach(processBody);
    if (data.createdAt) data.createdAt = new Date(data.createdAt);
    if (data.createdAt)
      data.lastEditedAt = data.lastEditedAt && new Date(data.lastEditedAt);
    if (data.startAt) data.startAt = new Date(data.startAt);
    if (data.expiresAt) data.expiresAt = new Date(data.expiresAt);
    if (data.endsAt) data.endsAt = new Date(data.endsAt);
    if (data.lastActionAt) data.lastActionAt = new Date(data.lastActionAt);
    if (data.archivedAt) data.archivedAt = new Date(data.archivedAt);
    if (data.participants) data.participants.forEach(processBody);
    if (data.comments) data.comments.forEach(processBody);
    if (data.schedule) data.schedule.forEach(processBody);
    if (data.startTime) data.startTime = timeStrToMinutes(data.startTime);
  }
  return data;
};

export const fetcher =
  (opts = {}) =>
  (url) =>
    window
      .fetch(`${import.meta.env.VITE_API_HOST}${url}`, {
        mode: "cors",
        credentials: "include",
        ...opts,
        headers: {
          "Content-Type": "application/json",
          ...opts.headers,
        },
      })
      .then((res) => res.json().then((body) => ({ res, body })))
      .then(({ res, body }) => {
        if (res.status === 401) {
          return null;
        } else if (res.status >= 400) {
          return Promise.reject({
            message: errorToString(body || res.statusText),
            status: res.status,
          });
        } else {
          return processBody(body);
        }
      });

const post = (url, data, mutateModels = []) =>
  fetcher({ method: "post", body: JSON.stringify(data) })(url).then((res) => {
    mutateModels.forEach((key) => {
      if (key) mutate(key);
    });
    return res;
  });

const swrOptions = {
  fetcher: fetcher(),
  onErrorRetry: (error) => {
    if (error.status && [401, 403, 404].includes(error.status)) return;
  },
};

export const ApiProvider = ({ children }) => (
  <SWRConfig value={swrOptions}>{children}</SWRConfig>
);
